import { FC, useState } from 'react'
import { RenderIf } from 'components/RenderIf'
import InfoItem from './InfoItem'

import { ShareButtons } from 'featureSliced/widgets/ShareButtons'
import { AGBModal } from 'featureSliced/features/TermsConditions/AGB'
import { TermsConditions } from 'featureSliced/features/TermsConditions'
import { PageType } from 'featureSliced/padges/MainScreen'
import { Button } from 'featureSliced/entities/Button'

import { ReactComponent as Account } from 'assets/account.svg'
import { ReactComponent as CrossImg } from 'assets/handlers/cross.svg'
import { ReactComponent as CellPhone } from 'assets/cover/cellPhone.svg'
import { ReactComponent as Marker } from 'assets/cover/markerStroke.svg'

import {
  AGBText,
  CoverSidebarCloseHandler,
  RestaurantInfoBanner,
  SidebarCloser,
  SidebarContainer,
  SidebarLink,
} from './styled'
import { useNavigate } from 'react-router-dom'
import { useOpenModal } from 'featureSliced/shared/hooks/useOpenModal'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { IconFull, IconStroked } from 'components/IconsContainers/styled'
import { getContrastColor } from 'featureSliced/shared/utils/getContrastColor'
import {
  interpolateNodes,
  nodeTokens,
} from 'featureSliced/shared/utils/interpolateNodes'

type SidebarProps = {
  isSidebarShowing: boolean
  openProfileModal: (page?: PageType) => void
  setIsSidebarShowing: (value: boolean) => void
  userName?: string | false
  openAuthModal: () => void
}

export const Sidebar: FC<SidebarProps> = ({
  isSidebarShowing,
  openProfileModal,
  setIsSidebarShowing,
  userName,
  openAuthModal,
}) => {
  // Hooks
  const t = useTranslations()
  const navigate = useNavigate()
  const openModal = useOpenModal()

  // Store
  const { companyData, address } = useMySelector((state) => state.app)
  const mainColor = useMySelector((state) => state.app.interfaceStyles.colour)
  const { isReservationsShown, isSocialsShown, loginAvailable } = useMySelector(
    (state) => state.app.menu
  )

  // Functions
  const navigateToReservation = () => {
    navigate('/reservation')
  }

  const openTermsAndConditions = (e: any) => {
    e.stopPropagation()
    openModal({
      id: 'TERMS_AND_CONDITIONS',
      title: t('sidebar.termsAndConditions'),
      components: [() => <TermsConditions />],
    })
  }

  const openAGB = (e: any) => {
    e.stopPropagation()
    openModal({
      id: 'AGB_MODAL',
      title: t('sidebar.generalTermsAndConditions'),
      components: [() => <AGBModal />],
    })
  }

  return (
    <>
      <SidebarCloser
        isSidebarShowing={isSidebarShowing}
        onClick={() => setIsSidebarShowing(false)}
      >
        <CoverSidebarCloseHandler isSidebarShowing={isSidebarShowing}>
          <Button buttonType="common" height="38px" width="38px">
            <IconStroked color={getContrastColor(mainColor)}>
              <CrossImg width="15" height="15" />
            </IconStroked>
          </Button>
        </CoverSidebarCloseHandler>
      </SidebarCloser>
      <SidebarContainer isSidebarShowing={isSidebarShowing}>
        <RenderIf condition={loginAvailable && !userName}>
          <InfoItem
            title={t('sidebar.account')}
            Icon={Account}
            data={[t('sidebar.logInSignUp')]}
            onClick={openAuthModal}
          />
        </RenderIf>
        <RenderIf condition={loginAvailable && !!userName}>
          <InfoItem
            title={t('sidebar.account')}
            Icon={Account}
            data={[userName]}
            // NOTE: callback is mandatory.
            onClick={() => openProfileModal()}
          />
        </RenderIf>
        {/*  */}
        <RenderIf condition={!!companyData.CompanyName}>
          <RestaurantInfoBanner>{companyData.CompanyName}</RestaurantInfoBanner>
        </RenderIf>
        <RenderIf
          condition={
            process.env.REACT_APP_IS_CLIC === '0' &&
            Boolean(isReservationsShown)
          }
        >
          <InfoItem
            title={t('profile.toReservate')}
            Icon={CellPhone}
            components={
              <>
                <SidebarLink href={`tel:${companyData.Phone}`}>
                  {companyData.Phone}
                </SidebarLink>
                <SidebarLink onClick={navigateToReservation}>
                  Online
                </SidebarLink>
              </>
            }
          />
        </RenderIf>
        <RenderIf
          condition={Boolean(
            companyData.CompanyName ||
              companyData.Street ||
              companyData.City ||
              companyData.PostCode
          )}
        >
          <InfoItem
            title={t('sidebar.address')}
            Icon={Marker}
            data={[
              companyData.CompanyName,
              companyData.Street,
              `${companyData.PostCode || ''}${
                companyData.PostCode ? ', ' : ''
              }${companyData.City || ''}`,
            ]}
          />
        </RenderIf>
        <RenderIf
          condition={
            process.env.REACT_APP_IS_CLIC === '0' &&
            Boolean(!isReservationsShown)
          }
        >
          <InfoItem
            title={t('tableReservation.contactInfo')}
            Icon={CellPhone}
            components={
              <SidebarLink href={`tel:${address.Phone}`}>
                {address.Phone}
              </SidebarLink>
            }
            data={[address.Mail, address.Website]}
          />
        </RenderIf>
        <RenderIf condition={Boolean(isSocialsShown)}>
          <ShareButtons />
        </RenderIf>
        <AGBText>
          {interpolateNodes(
            t('terms.sidebarLinks', nodeTokens(['agb', 'privacy'])),
            {
              agb: (
                <span aria-hidden="true" onClick={openAGB}>
                  {t('terms.agbLink')}
                </span>
              ),
              privacy: (
                <span aria-hidden="true" onClick={openTermsAndConditions}>
                  {t('terms.dsgvoLink')}
                </span>
              ),
            }
          )}{' '}
        </AGBText>
      </SidebarContainer>
    </>
  )
}
